import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '../axios';

const AuthContext = createContext();

const clearStoredAuth = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userId');
  localStorage.removeItem('isLoggedIn');
  document.cookie = 'access_token=; Max-Age=0; path=/;';
};

const applyAuthCheckResponse = (response) => {
  const isAuthenticated = response?.status === 200 && Boolean(response?.data?.user);

  if (!isAuthenticated) {
    throw new Error('No active authenticated session');
  }

  return {
    isAuthed: true,
    isLoading: false,
    user: response.data.user,
    subscriptionStatus: response.data.subscription_status || 'inactive',
  };
};

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState({
    isAuthed: false,
    isLoading: true,
    user: null,
    subscriptionStatus: null
  });

  const refreshAuth = useCallback(async () => {
    try {
      const response = await api.get('/api/check_auth', { withCredentials: true });
      setAuth(applyAuthCheckResponse(response));
    } catch {
      clearStoredAuth();
      setAuth({ isAuthed: false, isLoading: false, user: null, subscriptionStatus: null });
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/logout', {}, { __isRetryRequest: true });
    } catch {
      // Client-side cleanup is still enough to end the session locally.
    } finally {
      clearStoredAuth();
      setAuth({ isAuthed: false, isLoading: false, user: null, subscriptionStatus: null });
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const response = await api.get('/api/check_auth', { withCredentials: true });
        if (mounted) {
          setAuth(applyAuthCheckResponse(response));
        }
      } catch {
        clearStoredAuth();
        if (mounted) setAuth({ isAuthed: false, isLoading: false, user: null, subscriptionStatus: null });
      }
    })();
    return () => { mounted = false; };
  }, []);

  return <AuthContext.Provider value={{ ...auth, setAuth, refreshAuth, logout }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
